import { CalendarRangePicker } from '@shared/components/CalendarRangePicker';
import { Box, FormControl, TextV2 } from '@withjoy/joykit';
import { useField } from 'formik';
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { SchemaDefaultValues } from './helpers';
import { Calendar } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { DateRange } from '@shared/components/CalendarRangePicker/CalendarRangePicker';
import { useSearchFormConfig } from '../LocationDatesOccupantsSearchBar';
import { useFormattedDateRange } from '@shared/hooks/useFormattedDateRange';

const schema = Yup.object({
  startDate: Yup.date(),
  endDate: Yup.date(),
}).test('dates', 'Invalid dates', function (value) {
  if (!value) {
    return false;
  }
  return value.startDate && value.endDate;
});
// TODO: type this based on Yup.InferType<typeof schema> in a way that works with eslint
type ISchema = {
  startDate: Date | null;
  endDate: Date | null;
};

const initialValue: SchemaDefaultValues<typeof schema> = {
  startDate: undefined,
  endDate: undefined,
};

const Fields = ({ fieldsId, defaultEndDate, defaultStartDate }: { fieldsId: string; defaultStartDate?: Date; defaultEndDate?: Date }) => {
  // using DateRange as an uncontrolled component
  const [field, meta, helper] = useField<ISchema>(fieldsId);
  const { isOpenedFormView, isCompactVersion } = useSearchFormConfig();

  const { tq } = useTranslation('stays');

  const dateRangeDisplay = useFormattedDateRange(field.value.startDate, field.value.endDate);
  const handleChange = ([start, end]: Readonly<[Date | undefined, Date | undefined]>) => {
    helper.setValue({
      startDate: start || null,
      endDate: end || null,
    });
  };
  const errorProps = useMemo(() => {
    return {
      isInvalid: meta.touched && !!meta.error,
      error: (meta.touched && meta.error) || undefined,
    };
  }, [meta.touched, meta.error]);

  return (
    <FormControl
      {...errorProps}
      zIndex="sticky"
      marginTop={isCompactVersion ? 0 : { _: isOpenedFormView ? 8 : 0, md: 0 }}
      paddingY={isCompactVersion ? 0 : { _: 2, md: 0 }}
    >
      {isOpenedFormView ? (
        <>
          <TextV2
            typographyVariant={'hed3'}
            fontSize={19}
          >
            {tq`Select dates`}
          </TextV2>
          <DateRange
            onChange={handleChange}
            initial={[field.value.startDate || defaultStartDate, field.value.endDate || defaultEndDate]}
          />
        </>
      ) : (
        <CalendarRangePicker
          initial={[field.value.startDate || defaultStartDate, field.value.endDate || defaultEndDate]}
          onChange={handleChange}
          closeOnSelectDate
        >
          <FakeInput>{dateRangeDisplay}</FakeInput>
        </CalendarRangePicker>
      )}
    </FormControl>
  );
};

const FakeInput = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children, ...restProps }, ref) => {
  const { isCompactVersion } = useSearchFormConfig();

  return (
    <Box
      ref={ref}
      // spread required for FakeInput to work with PopoverV2
      {...restProps}
      display={'flex'}
      alignItems={'center'}
      cursor={'pointer'}
      borderRadius={'0.25rem'}
      paddingX={isCompactVersion ? 4 : { _: 4, md: '1rem' }}
      whiteSpace={'nowrap'}
      gap={isCompactVersion ? 5 : { _: 5, md: 3 }}
      typographyVariant={'label2'}
    >
      <Calendar />
      {children}
    </Box>
  );
});

FakeInput.displayName = 'FakeInput';

export { Fields, schema, initialValue };
